/**
 * Ein Channel im Kanal-Akkordeon (Schritt 2): Feld-Tabelle mit Rezept je
 * Texttyp (Spec 2026-07-24 §2/§4). Die Bullet-Warnung steht direkt unter der
 * betroffenen Zeile, nicht als Fusszeile — sie gehoert zu genau diesem Feld.
 */

import { useTranslation } from 'react-i18next'

import type { SelectOption } from '@/components/v2/ui'
import type { BulkAttributeCode } from '@/lib/api/channel-content-bulk'
import type { Channel } from '@/lib/api/channels'

import { ALL_ATTRIBUTE_CODES } from './attributeDefaults'
import { ChannelBulletWarning } from './ChannelBulletWarning'
import { ChannelFieldAccordionHeader } from './ChannelFieldAccordionHeader'
import { ChannelFieldRow } from './ChannelFieldRow'
import { useBulletMappingWarning } from './useBulletMappingWarning'
import { useChannelCredits } from './useChannelCredits'
import type { FieldSelection } from './useFieldSelections'

const EMPTY_FIELD: FieldSelection = {
  enabled: false,
  templateId: null,
  systemToolCode: null,
  rememberDefault: false,
}

interface ChannelFieldAccordionProps {
  channel: Channel
  fields: Record<BulkAttributeCode, FieldSelection>
  onFieldChange: (attributeCode: BulkAttributeCode, patch: Partial<FieldSelection>) => void
  templateOptions: SelectOption[]
  /** GOKI-Standard-Tools (TOOL_DEFINITIONS) — exklusiv zu templateOptions, per Prefix unterschieden. */
  toolOptions?: SelectOption[]
  productIds: string[]
  open: boolean
  onToggle: () => void
}

export function ChannelFieldAccordion({
  channel,
  fields,
  onFieldChange,
  templateOptions,
  toolOptions = [],
  productIds,
  open,
  onToggle,
}: ChannelFieldAccordionProps) {
  const { t } = useTranslation()
  const channelLabel = channel.custom_name || channel.channel_name
  const { missing: bulletMappingMissing } = useBulletMappingWarning(channel.id)

  const enabledCodes = ALL_ATTRIBUTE_CODES.filter((code) => fields[code]?.enabled)
  const showBulletWarning = Boolean(fields.bullets?.enabled) && bulletMappingMissing
  const { credits, texts } = useChannelCredits(
    productIds,
    enabledCodes.map((code) => ({
      channel_id: channel.id,
      attribute_code: code,
      template_id: fields[code]?.templateId ?? null,
      system_tool_code: fields[code]?.systemToolCode ?? null,
    })),
  )

  return (
    <div className="rounded-lg border border-neutral-200 dark:border-neutral-800">
      <ChannelFieldAccordionHeader
        channelId={channel.id}
        channelLabel={channelLabel}
        open={open}
        selectedCount={enabledCodes.length}
        totalCount={ALL_ATTRIBUTE_CODES.length}
        warningCount={showBulletWarning ? 1 : 0}
        selectedLabels={enabledCodes.map((code) => t(`channelContent.card.attribute.${code}`))}
        credits={credits}
        onToggle={onToggle}
        onSelectAll={() =>
          ALL_ATTRIBUTE_CODES.forEach((code) => onFieldChange(code, { enabled: true }))
        }
      />

      {open && (
        <>
          <div className="grid grid-cols-[minmax(9rem,14rem)_1fr_minmax(8rem,12rem)] gap-4 border-y border-neutral-200 px-4 py-2 text-2xs font-medium uppercase tracking-wide text-neutral-500 dark:border-neutral-800 dark:text-neutral-400">
            <span>{t('channelContent.wizard.colField')}</span>
            <span>{t('channelContent.wizard.colRecipe')}</span>
            <span>{t('channelContent.wizard.colRemember')}</span>
          </div>

          <div className="divide-y divide-neutral-100 dark:divide-neutral-900">
            {ALL_ATTRIBUTE_CODES.map((code) => (
              <div key={code}>
                <ChannelFieldRow
                  channelId={channel.id}
                  channelLabel={channelLabel}
                  code={code}
                  field={fields[code] ?? EMPTY_FIELD}
                  templateOptions={templateOptions}
                  toolOptions={toolOptions}
                  onFieldChange={(patch) => onFieldChange(code, patch)}
                />
                {code === 'bullets' && showBulletWarning && (
                  <ChannelBulletWarning channelLabel={channelLabel} />
                )}
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between border-t border-neutral-200 px-4 py-2 text-sm text-neutral-500 dark:border-neutral-800 dark:text-neutral-400">
            <span>{t('channelContent.wizard.channelTexts', { count: texts, channel: channelLabel })}</span>
            {credits !== null && (
              <span>{t('channelContent.wizard.approxCredits', { count: credits })}</span>
            )}
          </div>
        </>
      )}
    </div>
  )
}
